import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
  Stack,
  Alert,
  Divider,
  CircularProgress,
} from "@mui/material";
import ApexCharts from "react-apexcharts";
import { useQuerySurveyCorrelationMutation } from "../../../features/surveys/surveysApiSlice";
import Iconify from "../../../components/iconify";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const NUMERIC_TYPES = ["points", "opinion", "smiley"];
const CHOICE_TYPES = ["select", "singleChoice", "multipleChoice"];
const FILTERABLE_TYPES = [...NUMERIC_TYPES, ...CHOICE_TYPES];

const OPERATOR_LABELS = {
  includesAny: "ist einer von",
  includesAll: "enth\u00e4lt alle von",
  equals: "ist genau",
  greaterThan: "gr\u00f6\u00dfer als",
  lessThan: "kleiner als",
};

const OPINION_LABELS = {
  "1": "Stimme \u00fcberhaupt nicht zu",
  "2": "Stimme nicht zu",
  "3": "Neutral",
  "4": "Stimme zu",
  "5": "Stimme voll und ganz zu",
};

const SMILEY_LABELS = {
  "1": "1 \u2013 \uD83D\uDE21",
  "2": "2 \u2013 \uD83D\uDE20",
  "3": "3 \u2013 \uD83D\uDE10",
  "4": "4 \u2013 \uD83D\uDE42",
  "5": "5 \u2013 \uD83D\uDE03",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getOperatorsForType(type) {
  if (NUMERIC_TYPES.includes(type)) {
    return ["includesAny", "equals", "greaterThan", "lessThan"];
  }
  if (type === "multipleChoice") {
    return ["includesAny", "includesAll"];
  }
  return ["includesAny", "equals"];
}

function getValueOptions(question) {
  if (!question) return [];
  switch (question.type) {
    case "points":
      return Array.from({ length: 10 }, (_, i) => ({
        value: String(i + 1),
        label: String(i + 1),
      }));
    case "opinion":
      return Object.entries(OPINION_LABELS).map(([value, label]) => ({
        value,
        label: `${value} \u2013 ${label}`,
      }));
    case "smiley":
      return Object.entries(SMILEY_LABELS).map(([value, label]) => ({
        value,
        label,
      }));
    case "select":
    case "singleChoice":
    case "multipleChoice":
      return (question.options || []).map((opt) => ({
        value: opt.value,
        label: opt.label,
      }));
    default:
      return [];
  }
}

function isMultiSelect(operator) {
  return operator === "includesAny" || operator === "includesAll";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SurveyCorrelationAnalysis = ({ surveyId, surveyQuestions }) => {
  const [filterQuestionId, setFilterQuestionId] = useState("");
  const [filterOperator, setFilterOperator] = useState("includesAny");
  const [filterValues, setFilterValues] = useState([]);
  const [targetQuestionId, setTargetQuestionId] = useState("");

  const [querySurveyCorrelation, { data: result, isLoading, error }] =
    useQuerySurveyCorrelationMutation();

  // Questions that can be used as filter or target (exclude openQuestion)
  const filterableQuestions = useMemo(
    () => surveyQuestions.filter((q) => FILTERABLE_TYPES.includes(q.type)),
    [surveyQuestions]
  );

  const targetableQuestions = useMemo(
    () =>
      surveyQuestions.filter(
        (q) => FILTERABLE_TYPES.includes(q.type) && q.id !== filterQuestionId
      ),
    [surveyQuestions, filterQuestionId]
  );

  const filterQuestion = surveyQuestions.find(
    (q) => q.id === filterQuestionId
  );
  const valueOptions = useMemo(
    () => getValueOptions(filterQuestion),
    [filterQuestion]
  );
  const operators = useMemo(() => {
    if (!filterQuestion) return [];
    return getOperatorsForType(filterQuestion.type);
  }, [filterQuestion]);

  // --- Event handlers ---

  const handleFilterQuestionChange = (e) => {
    setFilterQuestionId(e.target.value);
    setFilterOperator("includesAny");
    setFilterValues([]);
    // Reset target if it matches the new filter
    if (e.target.value === targetQuestionId) {
      setTargetQuestionId("");
    }
  };

  const handleFilterOperatorChange = (e) => {
    setFilterOperator(e.target.value);
    setFilterValues([]);
  };

  const handleFilterValuesChange = (e) => {
    const val = e.target.value;
    if (isMultiSelect(filterOperator)) {
      setFilterValues(typeof val === "string" ? val.split(",") : val);
    } else {
      setFilterValues([val]);
    }
  };

  const handleSingleValueChange = (e) => {
    setFilterValues([e.target.value]);
  };

  const canAnalyze =
    filterQuestionId && filterValues.length > 0 && targetQuestionId;

  const handleAnalyze = async () => {
    if (!canAnalyze) return;
    await querySurveyCorrelation({
      surveyId,
      filters: [
        {
          questionId: filterQuestionId,
          operator: filterOperator,
          values: filterValues,
        },
      ],
      target: { questionId: targetQuestionId },
    });
  };

  // --- Chart rendering ---

  const renderResult = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error?.data?.message || "Korrelationsabfrage fehlgeschlagen."}
        </Alert>
      );
    }

    if (!result) return null;

    const {
      totalDistribution,
      filteredDistribution,
      totalCount,
      filteredCount,
      filterPercentage,
    } = result;

    if (totalCount === 0) {
      return (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Keine Antworten vorhanden.
        </Alert>
      );
    }

    const labels = totalDistribution.map((d) => d.label);
    const totalPercentages = totalDistribution.map((d) => d.percentage);
    const filteredPercentages = filteredDistribution.map((d) => d.percentage);

    const chartOptions = {
      chart: {
        type: "bar",
        toolbar: { show: true, tools: { download: true } },
      },
      plotOptions: {
        bar: { horizontal: false, columnWidth: "55%" },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val}%`,
        style: { fontSize: "11px" },
      },
      xaxis: {
        categories: labels,
        labels: { style: { fontSize: "12px" } },
      },
      yaxis: {
        title: { text: "Anteil (%)" },
        max: 100,
        labels: { formatter: (val) => `${Math.round(val)}%` },
      },
      tooltip: {
        y: {
          formatter: (val, { seriesIndex, dataPointIndex }) => {
            const entry =
              seriesIndex === 0
                ? totalDistribution[dataPointIndex]
                : filteredDistribution[dataPointIndex];
            return `${val}% (${entry.count} Antwort${entry.count === 1 ? "" : "en"})`;
          },
        },
      },
      colors: ["#48aead", "#ff9800"],
      legend: { position: "top" },
    };

    const series = [
      {
        name: `Alle Antworten (${totalCount})`,
        data: totalPercentages,
      },
      {
        name: `Gefilterte Antworten (${filteredCount})`,
        data: filteredPercentages,
      },
    ];

    return (
      <Box sx={{ mt: 3 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>{filteredCount}</strong> von <strong>{totalCount}</strong>{" "}
          Antworten entsprechen dem Filter ({filterPercentage}%)
        </Alert>
        <ApexCharts
          options={chartOptions}
          series={series}
          type="bar"
          height={400}
        />
      </Box>
    );
  };

  // --- Main render ---

  return (
    <Card sx={{ my: 3 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          Korrelationsanalyse
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Filtern Sie Antworten nach einer Bedingung und vergleichen Sie die
          Verteilung einer Zielfrage mit der Gesamtverteilung.
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {/* Filter row */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Filter
        </Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 3 }}>
          {/* Filter question */}
          <FormControl sx={{ minWidth: 250 }} size="small">
            <InputLabel>Filterfrage</InputLabel>
            <Select
              value={filterQuestionId}
              label="Filterfrage"
              onChange={handleFilterQuestionChange}
            >
              {filterableQuestions.map((q) => (
                <MenuItem key={q.id} value={q.id}>
                  {q.question}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Operator */}
          <FormControl
            sx={{ minWidth: 160 }}
            size="small"
            disabled={!filterQuestionId}
          >
            <InputLabel>Operator</InputLabel>
            <Select
              value={filterOperator}
              label="Operator"
              onChange={handleFilterOperatorChange}
            >
              {operators.map((op) => (
                <MenuItem key={op} value={op}>
                  {OPERATOR_LABELS[op]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Filter values */}
          <FormControl
            sx={{ minWidth: 220 }}
            size="small"
            disabled={!filterQuestionId}
          >
            <InputLabel>Werte</InputLabel>
            {isMultiSelect(filterOperator) ? (
              <Select
                multiple
                value={filterValues}
                label="Werte"
                onChange={handleFilterValuesChange}
                renderValue={(selected) =>
                  selected
                    .map(
                      (v) =>
                        valueOptions.find((o) => o.value === v)?.label || v
                    )
                    .join(", ")
                }
              >
                {valueOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Checkbox checked={filterValues.includes(opt.value)} />
                    <ListItemText primary={opt.label} />
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Select
                value={filterValues[0] || ""}
                label="Werte"
                onChange={handleSingleValueChange}
              >
                {valueOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            )}
          </FormControl>
        </Stack>

        {/* Target question */}
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Zielfrage
        </Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
          <FormControl sx={{ minWidth: 350 }} size="small">
            <InputLabel>Zielfrage</InputLabel>
            <Select
              value={targetQuestionId}
              label="Zielfrage"
              onChange={(e) => setTargetQuestionId(e.target.value)}
            >
              {targetableQuestions.map((q) => (
                <MenuItem key={q.id} value={q.id}>
                  {q.question}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            disabled={!canAnalyze || isLoading}
            onClick={handleAnalyze}
            startIcon={
              isLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Iconify icon="mdi:chart-bar" />
              )
            }
          >
            Analysieren
          </Button>
        </Stack>

        {/* Results */}
        {renderResult()}
      </CardContent>
    </Card>
  );
};

export default SurveyCorrelationAnalysis;
